/**
 * API Client Service
 *
 * Centralized API client with Firebase authentication
 * Automatically injects Firebase JWT token into requests
 */

import { getIdToken } from '@/lib/firebase'
import { logError } from '@/services/errorLogger'

// API Gateway base URL
const isDev = import.meta.env.DEV
const API_BASE = isDev
  ? '' // Vite proxy handles routing
  : 'https://8plbeu8soh.execute-api.us-east-1.amazonaws.com/prod'

export interface ApiError {
  message: string
  statusCode: number
}

export class ApiClientError extends Error {
  statusCode: number

  constructor(message: string, statusCode: number) {
    super(message)
    this.name = 'ApiClientError'
    this.statusCode = statusCode
  }
}

/**
 * Make an authenticated API request
 *
 * @param endpoint - API endpoint (e.g., '/api/user/profile')
 * @param options - Fetch options
 * @returns Parsed JSON response
 */
export async function apiClient<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getIdToken()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const url = `${API_BASE}${endpoint}`
  console.log(`[API] ${options.method || 'GET'} ${endpoint}`)

  const response = await fetch(url, {
    ...options,
    headers,
  })

  if (!response.ok) {
    let errorMessage = `Request failed: ${response.status}`
    try {
      const errorData = await response.json()
      errorMessage = errorData.message || errorData.error || errorMessage
    } catch {
      // Response is not JSON
    }
    console.error(`[API] Error: ${errorMessage}`)
    logError('API_ERROR', errorMessage, `${options.method || 'GET'} ${endpoint} → ${response.status}`)
    throw new ApiClientError(errorMessage, response.status)
  }

  const data = await response.json()
  return data as T
}

// ============ User Profile API ============

export interface UserProfile {
  userId: string
  email: string
  displayName?: string
  photoURL?: string
  hasPaid: boolean
  analysisCount: number
  createdAt: string
  updatedAt: string
  lastLoginAt: string
}

/**
 * Sync user profile on login (create if not exists)
 */
export async function syncUserProfile(userData: {
  email: string
  displayName?: string
  photoURL?: string
}): Promise<UserProfile> {
  return apiClient<UserProfile>('/api/user/profile', {
    method: 'POST',
    body: JSON.stringify(userData),
  })
}

/**
 * Get current user's profile
 */
export async function getUserProfile(): Promise<UserProfile> {
  return apiClient<UserProfile>('/api/user/profile')
}

/**
 * Update user profile
 */
export async function updateUserProfile(
  updates: Partial<Pick<UserProfile, 'displayName' | 'photoURL' | 'hasPaid'>>
): Promise<UserProfile> {
  return apiClient<UserProfile>('/api/user/profile', {
    method: 'PUT',
    body: JSON.stringify(updates),
  })
}

// ============ Analysis API ============

export interface AnalysisSummary {
  analysisId: string
  photoId: string
  mode: 'teaser' | 'full'
  skinAge: number
  skinScore: number
  createdAt: string
}

export interface AnalysisRecord extends AnalysisSummary {
  userId: string
  metrics: Record<string, number>
  heatmaps?: Record<string, string>
  surveyData: Record<string, unknown>
}

export interface SaveAnalysisRequest {
  photoId: string
  mode: 'teaser' | 'full'
  skinAge: number
  skinScore: number
  metrics: Record<string, number>
  heatmaps?: Record<string, string>
  surveyData: Record<string, unknown>
}

/**
 * Save analysis results to backend
 */
export async function saveAnalysis(
  data: SaveAnalysisRequest
): Promise<{ analysisId: string; createdAt: string }> {
  return apiClient('/api/analysis', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * Get user's analysis history
 */
export async function getAnalysisHistory(): Promise<{
  analyses: AnalysisSummary[]
  count: number
}> {
  return apiClient('/api/analysis')
}

/**
 * Get specific analysis by ID
 */
export async function getAnalysis(analysisId: string): Promise<AnalysisRecord> {
  return apiClient(`/api/analysis/${analysisId}`)
}

/**
 * Delete teaser analysis by photoId
 * Called when full analysis completes to remove redundant teaser data
 */
export async function deleteTeaserAnalysis(
  photoId: string
): Promise<{ deleted: boolean; deletedCount?: number }> {
  return apiClient(`/api/analysis/teaser/${photoId}`, {
    method: 'DELETE',
  })
}

// ============ Photo API ============

interface PhotoUploadResponse {
  photoId: string
  uploadUrl?: string
  expiresIn?: number
}

interface PhotoDownloadResponse {
  photoId: string
  downloadUrl: string
  expiresIn: number
}

/**
 * Get presigned upload URL for photo
 * (Authenticated version - associates photo with user)
 */
export async function getPhotoUploadUrl(): Promise<PhotoUploadResponse> {
  return apiClient('/api/photo/upload', {
    method: 'POST',
  })
}

/**
 * Upload photo directly via base64
 */
export async function uploadPhotoDirect(
  imageBase64: string
): Promise<{ photoId: string; size: number }> {
  return apiClient('/api/photo/upload-direct', {
    method: 'POST',
    body: JSON.stringify({ image: imageBase64 }),
  })
}

/**
 * Get presigned download URL for photo
 */
export async function getPhotoDownloadUrl(
  photoId: string
): Promise<PhotoDownloadResponse> {
  return apiClient(`/api/photo/${photoId}`)
}

// ============ Payment API ============

interface PaymentIntentResponse {
  clientSecret: string
}

/**
 * Create a Stripe PaymentIntent for $99 one-time payment
 *
 * @param photoId - Optional photoId to associate with payment (for photo migration)
 * @returns Client secret for Stripe.js
 */
export async function createPaymentIntent(
  photoId?: string
): Promise<PaymentIntentResponse> {
  return apiClient('/api/payment/create-intent', {
    method: 'POST',
    body: JSON.stringify({ photoId }),
  })
}
